import { scrubTokens } from './posthog';

/**
 * Google Ads conversion tracking: the Google tag, Consent Mode v2, and the
 * two conversions the site reports — an audit request submitted, and a
 * sample-report download. The third, a paid audit, is uploaded offline from
 * the admin console (`GET /admin/services/conversions`), keyed on the click id
 * `attribution.ts` sends with the request.
 *
 * Off unless `VITE_GOOGLE_ADS_ID` is set: no tag, no banner, no errors. Each
 * conversion is also off until its own label is set.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

function config() {
  const env = (key: string) =>
    (import.meta.env[key] as string | undefined)?.trim() || undefined;
  return {
    id: env('VITE_GOOGLE_ADS_ID'),
    leadLabel: env('VITE_GOOGLE_ADS_LEAD_LABEL'),
    sampleLabel: env('VITE_GOOGLE_ADS_SAMPLE_LABEL'),
  };
}

export const adsEnabled = (): boolean => Boolean(config().id);

/** EEA, UK and Switzerland: ad storage is denied until the visitor accepts.
 *  Google resolves the visitor's region; the site never sees it. */
export const CONSENT_REQUIRED_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
]; // prettier-ignore

export const SAMPLE_REPORT_PATH = '/sample-report.pdf';

// gtag.js reads the `arguments` object itself, not an array copy of it.
function gtag(..._args: unknown[]): void {
  if (!window.dataLayer) return;
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

const consentFor = (choice: AdsConsent) => ({
  ad_personalization: choice,
  ad_storage: choice,
  ad_user_data: choice,
  analytics_storage: choice,
});

// ── Consent ──────────────────────────────────────────────────────────────

export type AdsConsent = 'denied' | 'granted';

const CONSENT_KEY = 'floto-ads-consent';

export interface ConsentState {
  /** The visitor's stored choice; null until they make one. */
  choice: AdsConsent | null;
  /** Whether the banner is showing. */
  open: boolean;
}

let state: ConsentState = { choice: null, open: false };
const listeners = new Set<() => void>();
/** Where focus goes back to when the banner was opened from the footer. */
let returnFocusTo: HTMLElement | null = null;

function setState(next: ConsentState) {
  state = next;
  for (const l of listeners) l();
}

export const consentState = (): ConsentState => state;

export function subscribeConsent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function readStoredConsent(): AdsConsent | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

export function setConsent(choice: AdsConsent): void {
  try {
    localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    // Storage blocked: the choice holds for this visit only.
  }
  gtag('consent', 'update', consentFor(choice));
  setState({ choice, open: false });
}

/** The footer's "Cookie settings". */
export function openConsentSettings(): void {
  returnFocusTo =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  setState({ ...state, open: true });
}

/** Where the banner sends focus once it closes. */
export function takeReturnFocus(): HTMLElement | null {
  const el = returnFocusTo;
  returnFocusTo = null;
  return el;
}

// ── The tag ──────────────────────────────────────────────────────────────

let loaded = false;

/**
 * Consent defaults first, then the tag — once per page load, so an SPA route
 * change never re-inserts it. A stored choice is applied before the script
 * arrives, so a returning visitor's first hit already carries it.
 */
export function initGoogleAds(): void {
  const { id } = config();
  if (!id || loaded) return;
  loaded = true;

  try {
    const choice = readStoredConsent();
    setState({ choice, open: choice === null });

    window.dataLayer = window.dataLayer ?? [];
    gtag('consent', 'default', {
      ...consentFor('denied'),
      region: CONSENT_REQUIRED_REGIONS,
      wait_for_update: 500,
    });
    gtag('consent', 'default', consentFor('granted'));
    if (choice) gtag('consent', 'update', consentFor(choice));
    // With ad storage denied, strip click ids from the cookieless pings too.
    gtag('set', 'ads_data_redaction', true);

    gtag('js', new Date());
    // `/r/:token` is the customer's status-page login; it never reaches
    // Google any more than PostHog (`scrubTokens`).
    gtag('config', id, {
      page_location: scrubTokens(window.location.href),
      page_referrer: scrubTokens(document.referrer),
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);

    document.addEventListener('click', onSampleReportClick, true);
  } catch {
    // Ad measurement must never stop the site loading.
  }
}

/** Keeps the URL Google sees in step with the SPA's route, token scrubbed. */
export function setAdsPage(href: string): void {
  if (!loaded) return;
  gtag('set', { page_location: scrubTokens(href) });
}

// ── Conversions ──────────────────────────────────────────────────────────

/**
 * "Audit request submitted". Called from the form's success path only — never
 * from `/r/:token`, which people revisit. `transaction_id` dedupes retries;
 * it's the request reference, not the status token, which is a credential.
 */
export function trackLeadConversion(reference: string): void {
  const { id, leadLabel } = config();
  if (!loaded || !id || !leadLabel) return;
  gtag('event', 'conversion', {
    send_to: `${id}/${leadLabel}`,
    transaction_id: reference,
  });
}

/** "Sample report download", on any link to the PDF. Navigation is never
 *  held up: the beacon goes out as the page moves on. */
function onSampleReportClick(event: MouseEvent): void {
  const { id, sampleLabel } = config();
  if (!id || !sampleLabel) return;
  const target = event.target;
  const link = target instanceof Element ? target.closest('a[href]') : null;
  if (!(link instanceof HTMLAnchorElement)) return;
  if (
    link.origin !== window.location.origin ||
    link.pathname !== SAMPLE_REPORT_PATH
  ) {
    return;
  }
  gtag('event', 'conversion', {
    send_to: `${id}/${sampleLabel}`,
    transport_type: 'beacon',
  });
}
